import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Table2,
  FileText,
  Users,
  UploadCloud,
  LogOut,
  BellRing,
  SlidersHorizontal,
  CalendarRange,
  ShieldCheck,
  UserCog,
  Wallet,
} from "lucide-react";
import { LOGO } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", section: "dashboard" },
  { to: "/entries", label: "Daily Sheet", icon: Table2, testid: "nav-entries", section: "daily" },
  { to: "/monthly", label: "Monthly Sheet", icon: CalendarRange, testid: "nav-monthly", section: "monthly" },
  { to: "/validation", label: "Validation & Payment", icon: ShieldCheck, testid: "nav-validation", section: "validation" },
  { to: "/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices", section: "invoices" },
  { to: "/expenses", label: "Expenses", icon: Wallet, testid: "nav-expenses", section: "expenses" },
  { to: "/reminders", label: "Reminders", icon: BellRing, testid: "nav-reminders", section: "validation" },
  { to: "/sheets", label: "Sheet Sync", icon: UploadCloud, testid: "nav-sheets", section: "sheets" },
  { to: "/masters", label: "Masters", icon: Users, testid: "nav-masters", section: "masters" },
  { to: "/team", label: "Team & Access", icon: UserCog, testid: "nav-team", section: "team" },
  { to: "/settings", label: "Settings", icon: SlidersHorizontal, testid: "nav-settings", section: "settings" },
];

export const Layout = ({ children }) => {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const [dueCount, setDueCount] = useState(0);

  useEffect(() => {
    api
      .get("/reminders")
      .then((r) => setDueCount(r.data?.counts?.total || 0))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="flex h-16 items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-3">
            <img
              src={LOGO}
              alt="Ref Revenue"
              className="h-10 w-10 rounded-full object-cover"
              data-testid="brand-logo"
            />
            <div className="leading-none">
              <div className="font-display text-lg font-black tracking-tight">Ref Revenue</div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                Ads P&amp;L Control
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="reminder-bell"
              onClick={() => navigate("/reminders")}
              className="relative flex h-9 w-9 items-center justify-center border border-slate-300 transition-colors hover:bg-slate-50"
              title={`${dueCount} items need follow-up`}
            >
              <BellRing className="h-4 w-4" />
              {dueCount > 0 && (
                <span
                  data-testid="reminder-bell-count"
                  className="absolute -right-2 -top-2 min-w-[20px] bg-red-600 px-1 text-[10px] font-bold leading-5 text-white"
                >
                  {dueCount > 99 ? "99+" : dueCount}
                </span>
              )}
            </button>
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold">{user?.name}</div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                {user?.role}
              </div>
            </div>
            <Button
              data-testid="logout-button"
              variant="outline"
              size="sm"
              className="rounded-md border-slate-300 transition-colors hover:bg-slate-50 active:scale-95"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
            >
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
        <nav className="flex gap-1 overflow-x-auto border-t border-slate-200 px-2 md:px-6" data-testid="main-nav">
          {NAV.filter(({ section }) => can(section)).map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? "border-slate-900 text-slate-900"
                    : "border-transparent text-slate-500 hover:text-slate-900"
                }`
              }
            >
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="px-4 py-8 md:px-8 md:py-10">{children}</main>
    </div>
  );
};
